import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { type FontAwesomeIconProps } from '@hivebrite/design-system/FontAwesomeIcon';

import HeaderLink from 'hb-react/frontoffice/components/AppHeader/HeaderLink';
import useHeaderTheme from 'hb-react/frontoffice/components/AppHeader/hooks/useHeaderTheme';

import { type ScopeType, type ScopeObject } from './types';
import { getSearchNavigationPath } from './utils';

interface GlassIconType {
  iconType?: FontAwesomeIconProps['iconType'];
}
interface SearchButtonType extends GlassIconType {
  ariaLabel: string;
  isHeaderLink?: boolean;
  onClick?: () => void;
  scopeObject?: ScopeObject;
  scopeType?: ScopeType;
  tooltip?: string;
}

const GlassIcon = ({ iconType = CATEGORIES.SOLID }: GlassIconType) => {
  const headerTheme = useHeaderTheme();

  return (
    <FontAwesomeIcon
      color={headerTheme.color}
      height="24px"
      iconName="magnifying-glass"
      iconType={iconType}
    />
  );
};

const SearchButton = ({
  ariaLabel,
  iconType = CATEGORIES.SOLID,
  isHeaderLink = false,
  scopeObject,
  scopeType,
  tooltip,
  onClick,
}: SearchButtonType) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Define path and options depending on a type of scope for search.
  const path = getSearchNavigationPath({ scopeType, scopeObject });
  // Current location needed for "Back" button on search page.
  const options = { state: { from: location } };
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(path, options);
    }
  };

  if (isHeaderLink) {
    return (
      <HeaderLink
        activeIcon={<GlassIcon iconType={CATEGORIES.SOLID} />}
        defaultIcon={<GlassIcon iconType={iconType} />}
        role="link"
        aria-label={ariaLabel}
        tooltip={tooltip}
        onClick={handleClick}
        fixedParent
      />
    );
  }

  return (
    <Button
      variant="secondary"
      icon={() => (
        <FontAwesomeIcon
          height="16px"
          iconName="magnifying-glass"
          iconType={iconType}
        />
      )}
      onClick={handleClick}
      screenReadersText={ariaLabel}
    />
  );
};

export default SearchButton;
